import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type Map from 'ol/Map';
import layerGroupsService, {
  GroupChange, GroupItemInput, GroupItemRef, GroupPerson, LayerGroup, LayerGroups, groupError, toInput,
} from '@/services/layerGroups.service';
import { useToast } from '@/components/common/Toast';

export const GROUPS_KEY = ['layerGroups'];

/** The groups I see, and the changes I can make to them, each refreshing the list (and telling me when refused). */
export function useLayerGroups() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const query = useQuery<LayerGroups>({ queryKey: GROUPS_KEY, queryFn: () => layerGroupsService.list(), retry: false });

  const done = () => queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
  const failed = (e: unknown) => {
    const code = groupError(e);
    addToast(t(`layerGroups.error.${code}`, { defaultValue: t('layerGroups.error.generic', { reason: code }) }), 'error');
  };
  const opts = { onSuccess: done, onError: failed };

  const create = useMutation({ mutationFn: (a: { name: string; items?: GroupItemInput[] }) => layerGroupsService.create(a.name, a.items), ...opts });
  const change = useMutation({ mutationFn: (a: { id: string; change: GroupChange }) => layerGroupsService.change(a.id, a.change), ...opts });
  const remove = useMutation({ mutationFn: (id: string) => layerGroupsService.remove(id), ...opts });
  const order = useMutation({ mutationFn: (ids: string[]) => layerGroupsService.order(ids), ...opts });
  const share = useMutation({
    mutationFn: (a: { id: string; company: boolean; people: GroupPerson[] }) => layerGroupsService.share(a.id, a.company, a.people),
    ...opts,
  });
  const view = useMutation({
    mutationFn: (a: { id: string; hidden?: boolean; collapsed?: boolean }) => layerGroupsService.view(a.id, a),
    ...opts,
  });
  const copy = useMutation({ mutationFn: (a: { id: string; name?: string }) => layerGroupsService.copy(a.id, a.name), ...opts });

  /** Puts a layer into one of my groups, or takes it out. */
  const toggleItem = (group: LayerGroup, item: GroupItemRef) => {
    const has = group.items.some((i) => i.kind === item.kind && i.ref === item.ref);
    const items = has
      ? group.items.filter((i) => !(i.kind === item.kind && i.ref === item.ref)).map(toInput)
      : [...group.items.map(toInput), { kind: item.kind, ref: item.ref }];
    change.mutate({ id: group.id, change: { items } });
  };

  return { query, create, change, remove, order, share, view, copy, toggleItem };
}

/** A platform layer's reference as a group keeps it: its GeoServer name with the workspace. */
export function platformRef(layer: { layerName: string; layerWorkspace?: { name?: string } | null }): string {
  return `${layer.layerWorkspace?.name || 'cite'}:${layer.layerName}`;
}

/**
 * The layers I asked to see only in my menus, not also in their usual place: refs by kind. Only my own groups count -
 * someone else's choice never moves a layer out of my tree.
 */
export function useOnlyInMyMenus(): { platform: Set<string>; company: Set<string> } {
  const { data } = useQuery<LayerGroups>({ queryKey: GROUPS_KEY, queryFn: () => layerGroupsService.list(), retry: false });
  return useMemo(() => {
    const platform = new Set<string>();
    const company = new Set<string>();
    for (const g of data?.groups ?? []) {
      if (!g.mine) continue;
      for (const i of g.items) {
        if (!i.onlyHere) continue;
        (i.kind === 'platform' ? platform : company).add(i.ref);
      }
    }
    return { platform, company };
  }, [data]);
}

/**
 * Redraws whoever uses it when a layer is added to or taken off the map, or switched on or off: the company layers'
 * eyes live on the map, and a group and a workspace show the same layer, so either must follow the other.
 */
export function useMapLayerChanges(map: Map | null): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!map) return;
    const bump = () => setVersion((v) => v + 1);
    const layers = map.getLayers();
    const watched = new Set<any>();
    const watch = (l: any) => {
      if (watched.has(l)) return;
      watched.add(l);
      l.on('change:visible', bump);
    };
    layers.forEach(watch);
    const added = (e: any) => { watch(e.element); bump(); };
    layers.on('add', added);
    layers.on('remove', bump);
    return () => {
      layers.un('add', added);
      layers.un('remove', bump);
      watched.forEach((l) => l.un('change:visible', bump));
    };
  }, [map]);
  return version;
}
